import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {string | null} */
  #originalPriceHTML = null;
  /** @type {Array<number>} */
  #juoSyncTimeouts = [];
  /** @type {boolean} */
  #isSubscriptionMode = false;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog, quick-add-dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('change', this.handleJuoChange);

    this.#snapshotPrice();
    // Apply current Juo state once the widget has rendered.
    requestAnimationFrame(() => this.syncFromJuo());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog, quick-add-dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('change', this.handleJuoChange);
    this.#clearJuoSyncTimeouts();
  }

  /**
   * Returns the live price container. Refs can briefly point at a detached
   * node immediately after replaceWith, so always query the current DOM.
   * @returns {HTMLElement | null}
   */
  #getPriceContainer() {
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    return priceContainer instanceof HTMLElement ? priceContainer : null;
  }

  #snapshotPrice() {
    const priceContainer = this.#getPriceContainer();
    if (priceContainer) {
      this.#originalPriceHTML = priceContainer.innerHTML;
    }
  }

  /**
   * Handles Juo subscription widget mode changes via event delegation.
   * @param {Event} event
   */
  handleJuoChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== 'mode') return;
    if (!target.closest('#juo-subscription-widget')) return;
    this.#isSubscriptionMode = target.value === 'subscription';
    this.applyJuoMode(this.#isSubscriptionMode);
  };

  /**
   * Reads the currently selected mode from the Juo widget in the same
   * section and applies it to the price container.
   */
  syncFromJuo() {
    const widget = this.#getJuoWidget();
    if (!widget) return;
    const checked = /** @type {HTMLInputElement|null} */ (
      widget.querySelector('input[name="mode"]:checked')
    );
    if (checked) {
      this.#isSubscriptionMode = checked.value === 'subscription';
    }
    this.applyJuoMode(this.#isSubscriptionMode);
  }

  /**
   * @returns {Element|null}
   */
  #getJuoWidget() {
    const closestSection = this.closest('.shopify-section, dialog, quick-add-dialog');
    if (!closestSection) return null;
    return closestSection.querySelector('#juo-subscription-widget');
  }

  /**
   * Applies the price display for the selected Juo mode.
   * @param {boolean} isSubscription
   */
  applyJuoMode(isSubscription) {
    const priceContainer = this.#getPriceContainer();
    if (!priceContainer) return;

    if (!isSubscription) {
      // Restore the original (one-time purchase) price markup.
      if (this.#originalPriceHTML != null) {
        priceContainer.innerHTML = this.#originalPriceHTML;
      }
      return;
    }

    const widget = this.#getJuoWidget();
    if (!widget) return;

    const subPriceEl = this.#getVisibleElement(
      widget,
      '[part~="price--subscription"] [part~="subscription-price"] .money'
    );
    if (!subPriceEl) return;

    const compareEl = this.#getVisibleElement(widget, '[part~="price--subscription"] .j-compare-price .money');

    const regularDiv = priceContainer.querySelector('.price__regular');
    const saleDiv = priceContainer.querySelector('.price__sale');

    if (saleDiv) {
      const saleSpan = saleDiv.querySelector('.price-item--sale.price');
      const compareSpan = saleDiv.querySelector('.compare-at-price');
      if (saleSpan) saleSpan.innerHTML = subPriceEl.innerHTML;
      if (compareSpan) compareSpan.innerHTML = compareEl ? compareEl.innerHTML : '';

      regularDiv?.classList.add('price__hidden');
      saleDiv.classList.remove('price__hidden');
      return;
    }

    // Fallback for simplified price markup variants (e.g. plain snippet output
    // without a `.price__sale` container).
    // Prefer `.price__regular .price` so we don't accidentally update unit-price
    // or other auxiliary price nodes.
    const primaryPrice =
      priceContainer.querySelector('.price__regular .price') ?? priceContainer.querySelector('.price');
    if (primaryPrice) {
      primaryPrice.innerHTML = subPriceEl.innerHTML;
    }

    const comparePrice = priceContainer.querySelector('.compare-at-price');
    if (comparePrice) {
      comparePrice.innerHTML = compareEl ? compareEl.innerHTML : '';
    }
  }

  /**
   * Prefer visible element to avoid stale/hidden prices in widget DOM.
   * @param {Element} root
   * @param {string} selector
   * @returns {HTMLElement|null}
   */
  #getVisibleElement(root, selector) {
    const matches = Array.from(root.querySelectorAll(selector));
    for (const match of matches) {
      if (!(match instanceof HTMLElement)) continue;
      const style = window.getComputedStyle(match);
      const isVisible = style.display !== 'none' && style.visibility !== 'hidden' && match.offsetParent !== null;
      if (isVisible) return match;
    }

    const first = matches[0];
    return first instanceof HTMLElement ? first : null;
  }

  #clearJuoSyncTimeouts() {
    for (const timeoutId of this.#juoSyncTimeouts) {
      clearTimeout(timeoutId);
    }
    this.#juoSyncTimeouts = [];
  }

  #scheduleJuoSyncRetries() {
    this.#clearJuoSyncTimeouts();
    // Juo can update price asynchronously after variant changes.
    const retryDelays = [0, 200, 500, 1000, 1500];
    for (const delay of retryDelays) {
      const timeoutId = window.setTimeout(() => this.syncFromJuo(), delay);
      this.#juoSyncTimeouts.push(timeoutId);
    }
  }

  /**
   * Finds the matching product-price element in fetched variant HTML.
   * @param {Document | Element} html
   * @returns {Element | null}
   */
  #findUpdatedProductPrice(html) {
    if (this.dataset.blockId) {
      const matchedByBlockId = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
      if (matchedByBlockId) return matchedByBlockId;
    }

    return (
      html.querySelector('[data-product-grid-content] product-price') ??
      html.querySelector('.shopify-section product-price') ??
      html.querySelector('.product-details product-price') ??
      html.querySelector('product-price')
    );
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    const { newProduct, productId: eventProductId } = event.detail.data;

    if (newProduct) {
      this.dataset.productId = newProduct.id;
    } else if (
      eventProductId &&
      this.dataset.productId &&
      eventProductId !== this.dataset.productId
    ) {
      return;
    }

    const priceContainer = this.#getPriceContainer();
    const volumePricingNote = this.querySelector('[ref="volumePricingNote"]');
    const newProductPrice = this.#findUpdatedProductPrice(event.detail.data.html);
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
      this.updatedCallback();
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
    const livePriceContainer = this.#getPriceContainer();

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      livePriceContainer?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
      this.updatedCallback();
    } else {
      volumePricingNote.replaceWith(newNote);
      this.updatedCallback();
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Re-snapshot the freshly rendered one-time price markup.
    this.#snapshotPrice();

    if (this.#isSubscriptionMode) {
      // Overlay subscription pricing; Juo may update asynchronously.
      this.applyJuoMode(true);
      this.#scheduleJuoSyncRetries();
    } else {
      // One-time purchase: server-rendered price is already correct in the DOM.
      // Do not restore from snapshot here — stale refs could revert it.
      this.#clearJuoSyncTimeouts();
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
